// ProjectDetail.jsx
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { mockData } from "../data/mock";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: i => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.6, ease: "easeOut" }
  })
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const projects = mockData.portfolio || [];
  const currentIndex = projects.findIndex(p => p.id.toString() === projectId);
  const project = projects[currentIndex];

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">Project Not Found</h2>
          <Link to="/portfolio" className="mt-4 inline-block text-[var(--color-accent)]">
            ← Back to portfolio
          </Link>
        </div>
      </div>
    );
  }
  const a = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "2rem"
};

  const nextProject = projects[(currentIndex + 1) % projects.length];
  const prevProject = projects[(currentIndex - 1 + projects.length) % projects.length];

  // safe images array
  const images = Array.isArray(project.images) ? project.images : [];

  return (
    <div className="min-h-screen bg-[var(--color-light)] text-[var(--color-dark)]">
      {/* HERO */}
      <header className="py-20 px-6 bg-gradient-to-br from-primary to-primary-dark pt-28">
        <div className="container max-w-6xl mx-auto">

            {/* GRID: left text, right image */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">

            {/* LEFT SIDE CONTENT */}
            <div>
                <Link 
                to="/portfolio" 
                className="inline-flex items-center gap-2 text-[var(--color-dark)]/80"
                >
                <ArrowLeft size={18} /> Back
                </Link>

                <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mt-6 text-5xl font-bold"
                style={{ fontFamily: "var(--font-serif)" }}
                >
                {project.title}
                </motion.h1>

                <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="mt-4 text-[var(--color-dark)]/80 max-w-xl leading-relaxed"
                >
                {project.intro || project.description}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-12 mb-10 max-w-3xl text-[var(--color-dark)]/80"
                >
                    <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: "var(--font-serif)" }}>
                    Project Description
                    </h3>
                    <p className="leading-relaxed">{project.description}</p>
                </motion.div>

                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-[var(--color-dark)]/70">
                <span className="bg-[var(--color-contrast)] text-white px-3 py-1 rounded-full">
                    {project.category}
                </span>
                <span>{project.year}</span>
                {/* {project.client && <span>• {project.client}</span>} */}
                </div>
            </div>

            {/* RIGHT SIDE IMAGE */}
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="w-half"
            >
                <img 
                src={project.image} 
                alt={project.title}
                className="rounded-xl shadow-lg w-full object-cover bg-black "
                />
            </motion.div>

            </div>
        </div>
     </header>


      {/* IMAGES LAYOUT */}
      <main className="py-16 px-6">
        <div className=" ">

          {/* PREMIUM CASE STUDY GALLERY */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">

            {images.map((img, i) => {
                const size = img.size || "half";

                // size rules:
                const isLarge = size === "large";
                const classes = isLarge ? "md:col-span-2" : "md:col-span-1";

                // height rules:
                let heightClass = "h-[300px]";
                if (size === "large") heightClass = "h-[440px] md:h-[680px]";
                if (size === "half") heightClass = "h-[300px] md:h-[420px]";
                if (size === "third") heightClass = "h-[180px] md:h-[260px]";

                return (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className={`${classes} group overflow-hidden rounded-[8px] shadow-md shadow-[rgba(140,112,81,0.15)] bg-black`}
                >
                    <img
                    src={img.src}
                    alt={img.alt || `case study img ${i}`}
                    loading="lazy"
                    className={`w-full object-cover ${heightClass} transition-transform duration-500 group-hover:scale-[1.02]`}
                    />
                </motion.div>
                );
            })}

            </div>


          {/* DESCRIPTION (long) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 max-w-3xl text-[var(--color-dark)]/80"
          >
            <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: "var(--font-serif)" }}>
              Project Description
            </h3>
            <p className="leading-relaxed">{project.description}</p>
          </motion.div>

          {/* NEXT / PREV */}
          <div className="mt-20 border-t pt-8 flex items-center justify-between">
            <button
              onClick={() => navigate(`/${prevProject.id}`)}
              className="flex items-center gap-2 px-4 py-2 rounded-md border hover:bg-[var(--color-primary)] transition"
            >
              <ArrowLeft size={16} /> Previous
            </button>

            <div className="text-sm text-[var(--color-dark)]/60">{currentIndex + 1} / {projects.length}</div>

            <button
              onClick={() => navigate(`/${nextProject.id}`)}
              className="flex items-center gap-2 px-4 py-2 rounded-md border hover:bg-[var(--color-primary)] transition"
            >
              Next <ArrowRight size={16} />
            </button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
